"use client"

import { useState } from "react"
import { useTranslation } from "react-i18next"
import { ChevronDown, ChevronRight, Check, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Avatar } from "@/components/ui/avatar"
import { useVisitHistorySection } from "@/hooks/modal/useVisitHistorySection"
import { useVisitConsumables } from "@/hooks/modal/useVisitConsumables"
import { useCurrencySymbol } from "@/hooks/shared/useCurrencySymbol"

export interface Visit {
  id: number
  /** The appointment's photos and videos (boona docs/codebase/attachments/) */
  attachments?: AttachmentItem[]
  date: string
  time: string
  employee: {
    name: string
    photo?: string
  }
  // Raw status slug for i18n translation
  status: "visited" | "waiting" | "no_show" | "cancelled"
  services: {
    id: number
    name: string
    cost: number
    paid: number
  }[]
  additionalItems?: {
    name: string
    quantity: number
    unit: string
    cost: number
  }[]
  comment?: string
  location: string
}

import { useAppointmentModal } from "@/contexts/AppointmentModalContext"
import { AttachmentChip } from "@/components/custom/attachments/AttachmentChip"
import { AttachmentViewer } from "@/components/custom/attachments/AttachmentViewer"
import type { AttachmentItem } from "@/components/custom/attachments/types"
import { deleteAttachment } from "@/lib/services/attachmentsService"

/**
 * Inner component to fetch and display consumables for a specific visit.
 * Groups consumables by service and displays them under each service name.
 */
function VisitConsumables({ visitId, services }: { visitId: number; services: { id: number; name: string }[] }) {
  const { t } = useTranslation()
  const currencySymbol = useCurrencySymbol()
  const { data: consumables, isLoading, error } = useVisitConsumables(visitId, true)

  if (isLoading) {
    return (
      <div className="grid grid-cols-12 gap-4 py-2 px-4 text-sm text-gray-500">
        <div className="col-span-3"></div>
        <div className="col-span-7 flex items-center">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          {t("appointment.history.loading_consumables", "Loading consumables...")}
        </div>
        <div className="col-span-2"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="grid grid-cols-12 gap-4 py-2 px-4 text-sm text-red-500">
        <div className="col-span-3"></div>
        <div className="col-span-7">
          {t("appointment.history.consumables_error", "Error loading consumables")}
        </div>
        <div className="col-span-2"></div>
      </div>
    )
  }

  if (!consumables || consumables.length === 0) {
    return (
      <div className="grid grid-cols-12 gap-4 py-2 px-4 text-sm text-gray-400">
        <div className="col-span-3"></div>
        <div className="col-span-7">
          {t("appointment.history.no_consumables", "No consumables")}
        </div>
        <div className="col-span-2"></div>
      </div>
    )
  }

  // Group consumables by service_id
  const consumablesByService = consumables.reduce((acc, item) => {
    const serviceId = item.service_id
    if (!acc[serviceId]) {
      acc[serviceId] = []
    }
    acc[serviceId].push(item)
    return acc
  }, {} as Record<number, typeof consumables>)

  return (
    <>
      {services.map((service) => {
        const serviceConsumables = consumablesByService[service.id]
        if (!serviceConsumables || serviceConsumables.length === 0) {
          return null
        }
        return (
          <div key={service.id} className="bg-gray-50 rounded-md mx-4 mb-2 p-2">
            {/* Service header */}
            <div className="text-xs font-medium text-gray-600 mb-1 pl-1">
              {service.name}
            </div>
            {/* Consumables for this service */}
            {serviceConsumables.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-4 py-0.5 px-1 text-sm text-gray-500">
                <div className="col-span-12">
                  • {item.title}, {item.amount} {item.unit}, {(item.price * item.amount).toFixed(2)} {currencySymbol}
                </div>
              </div>
            ))}
          </div>
        )
      })}
    </>
  )
}


export function VisitHistorySection() {
  const [filter, setFilter] = useState("all")
  const [expandedVisits, setExpandedVisits] = useState<number[]>([])
  // the photos and videos of a past visit, in the dark viewer; deleted ones leave at once
  const [viewer, setViewer] = useState<{ visitId: number; index: number } | null>(null)
  const [gone, setGone] = useState<Set<string>>(new Set())
  const filesOf = (visitId: number | undefined): AttachmentItem[] =>
    (visits.find((v) => v.id === visitId)?.attachments ?? []).filter((a) => !gone.has(a.id))

  const { appointment } = useAppointmentModal()
  const { t } = useTranslation()
  const currencySymbol = useCurrencySymbol()

  // Fetch visit history using the new React Query hook
  const companyId = appointment?.company_id
  const clientId = appointment?.client?.id

  const { data: visits = [], isLoading, error } = useVisitHistorySection(companyId, clientId)

  // Handler for toggling visit details
  const handleToggleVisitDetails = (visitId: number) => {
    setExpandedVisits((prev) =>
      prev.includes(visitId) ? prev.filter((id) => id !== visitId) : [...prev, visitId]
    )
  }

  // Handler for filter change
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilter(e.target.value)
  }

  // Extract unique statuses for the filter select
  const uniqueStatuses = Array.from(new Set(visits.map(v => v.status)))

  // Filter visits by selected status
  const filteredVisits = filter === "all" ? visits : visits.filter(v => v.status === filter)

  const statusLabel = (status: string) =>
    ({
      visited: t("appointment.status.arrived", "Arrived"),
      waiting: t("appointment.status.waiting", "Waiting"),
      no_show: t("appointment.status.no_show", "No show"),
      cancelled: t("appointment.visit.cancelled", "Cancelled"),
    } as Record<string, string>)[status] ?? status

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "waiting":
        return (
          <div className="w-5 h-5 rounded-full bg-sky-100 flex items-center justify-center text-sky-600">…</div>
        )
      case "cancelled":
        return (
          <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">×</div>
        )
      case "visited":
        return (
          <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center text-green-600">+</div>
        )
      case "no_show":
        return <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center text-red-600">-</div>
      default:
        return null
    }
  }

  // Group filtered visits by date
  // (moved below after loading/error checks)

  if (isLoading) {
    return (
      <div className="flex-1 overflow-y-auto flex items-center justify-center">
        <span>{t("appointment.history.loading", "Loading visit history...")}</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 overflow-y-auto flex items-center justify-center text-red-500">
        <span>{t("appointment.history.error", "Error loading visit history")}</span>
      </div>
    )
  }

  // Group filtered visits by date
  const visitsByDate: { [key: string]: Visit[] } = {}
  filteredVisits.forEach((visit) => {
    if (!visitsByDate[visit.date]) {
      visitsByDate[visit.date] = []
    }
    visitsByDate[visit.date].push(visit)
  })

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="p-6">
        {/* Filter dropdown */}
        <div className="mb-6">
          <div className="relative inline-block w-64">
            <select
              className="w-full h-9 px-3 border border-gray-300 rounded-md appearance-none text-sm"
              value={filter}
              onChange={handleFilterChange}
            >
              <option value="all">{t("appointment.history.show_all", "Show all appointments")}</option>
              {uniqueStatuses.map(status => (
                <option key={status} value={status}>{statusLabel(status)}</option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Table header */}
        <div className="grid grid-cols-12 gap-4 py-2 bg-gray-50 text-xs text-gray-500 rounded-t-md px-4">
          <div className="col-span-2">{t("appointment.history.employee", "Employee")}</div>
          <div className="col-span-1">{t("appointment.history.status", "Status")}</div>
          <div className="col-span-5">{t("appointment.history.service", "Service")}</div>
          <div className="col-span-2 text-right">{t("appointment.history.cost", "Cost")}</div>
          <div className="col-span-2 text-right">{t("appointment.history.paid", "Paid")}</div>
        </div>

        {/* Visit list */}
        <div className="border rounded-b-md">
          {Object.entries(visitsByDate).map(([date, visits]) => (
            <div key={date} className="border-b last:border-b-0">
              {/* Date header */}
              <div className="py-3 px-4 bg-gray-50 text-sm font-medium">{date}</div>

              {/* Visits for this date */}
              {visits.map((visit) => (
                <div key={visit.id} className="border-t">
                  {/* Condensed Visit Row(s) */}
                  {visit.services.length > 0 && (
                    <>
                      {/* First row: employee, status, first service */}
                      <div className="grid grid-cols-12 gap-4 py-3 px-4 text-sm items-center">
                        {/* Employee (col-span-2) */}
                        <div className="col-span-2 flex items-center">
                          <Avatar className="h-8 w-8 mr-2">
                            {visit.employee.photo ? (
                              <img
                                src={visit.employee.photo}
                                alt={visit.employee.name}
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              <div className="bg-gray-200 w-full h-full rounded-full flex items-center justify-center text-xs">
                                {visit.employee.name.charAt(0)}
                              </div>
                            )}
                          </Avatar>
                          <div>
                            <div className="text-sm font-medium">{visit.employee.name}</div>
                            <div className="text-xs text-gray-500">
                              {visit.time}{" "}
                              {visit.status === "visited" ? (
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 h-auto text-blue-600 text-xs"
                                  onClick={() => handleToggleVisitDetails(visit.id)}
                                >
                                  {t("appointment.history.details", "Details")} <ChevronRight className="h-3 w-3 ml-1" />
                                </Button>
                              ) : (
                                <span className="text-gray-400 text-xs cursor-not-allowed">
                                  {t("appointment.history.details", "Details")}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Status (col-span-1) */}
                        <div className="col-span-1 flex justify-center">{getStatusIcon(visit.status)}</div>
                        {/* Service name (col-span-5) */}
                        <div className="col-span-5 flex flex-col items-start">
                          <div className="flex items-center">
                            <Check className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                            <span>{visit.services[0]?.name ?? "—"}</span>
                          </div>
                          {visit.comment && (
                            <div className="text-xs text-gray-500 mt-1 ml-5">
                              {t("appointment.history.comment", "Comment:")}{" "}{visit.comment}
                            </div>
                          )}
                          {!!filesOf(visit.id).length && (
                            <AttachmentChip className="mt-2 ml-5" items={filesOf(visit.id)}
                                            onOpen={(i) => setViewer({ visitId: visit.id, index: i })} />
                          )}
                        </div>
                        {/* Service cost (col-span-2) */}
                        <div className="col-span-2 text-right">{visit.services[0]?.cost ?? 0} {currencySymbol}</div>
                        {/* Service paid (col-span-2) */}
                        <div className="col-span-2 text-right">{visit.services[0]?.paid ?? 0} {currencySymbol}</div>
                      </div>
                      {/* Additional service rows (if any) */}
                      {visit.services.slice(1).map((service, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-4 py-1 px-4 text-sm items-center">
                          <div className="col-span-2"></div>
                          <div className="col-span-1"></div>
                          <div className="col-span-5 flex items-center">
                            <Check className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                            <span>{service.name}</span>
                          </div>
                          <div className="col-span-2 text-right">{service.cost} {currencySymbol}</div>
                          <div className="col-span-2 text-right">{service.paid} {currencySymbol}</div>
                        </div>
                      ))}
                    </>
                  )}

                  {/* Consumables (fetched dynamically when expanded) */}
                  {visit.status === "visited" && expandedVisits.includes(visit.id) && (
                    <VisitConsumables visitId={visit.id} services={visit.services} />
                  )}

                  {/* Location */}
                  <div className="py-1 px-4 text-right text-xs text-gray-500 border-t">{visit.location}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <AttachmentViewer
        items={filesOf(viewer?.visitId)}
        index={viewer ? viewer.index : null}
        onIndexChange={(i) => setViewer((v) => (i === null || !v ? null : { ...v, index: i }))}
        onDelete={viewer && filesOf(viewer.visitId)[viewer.index]?.canDelete ? async (item) => {
          if (!window.confirm(t("attachments.delete_confirm", "Delete this file? It can't be undone."))) return
          await deleteAttachment(Number(item.id))
          const left = filesOf(viewer.visitId).filter((x) => x.id !== item.id)
          setGone((g) => new Set(g).add(item.id))
          setViewer(left.length ? { ...viewer, index: Math.min(viewer.index, left.length - 1) } : null)
        } : undefined}
      />
    </div>
  )
}
